import { useContext } from 'react';
import { DetailContext, UserContext, useEventCallback } from '@eas/common-web';
import { DeliveredDocument, InternalDocument, Me } from '@models';
import {
  Agenda,
  DeliveredDocumentState,
  EsFileState,
  Role,
  UIElement,
} from '@enums';
import { deliveredDocumentAccessMap } from './../document-delivered/delivered-document-access-map';
import { internalDocumentAccessMap } from './../document-internal/internal-document-access-map';
import { outgoingDocumentAccessMap } from './../document-outgoing/outgoing-document-access-map';

export const DOCUMENTS_ACCESS = [
  Role.MAILROOM, // Podatelna/výpravna
  Role.LUSTRUM, // Lustrum
  Role.HEAD_OSV, // Vedoucí OSV
  Role.INDEX, // Rejstřík
  Role.LAWYER, // Právník
  Role.SECRETARY, // Tajemnice
  Role.JUDGE, // Soudce
  Role.ASSISTANT, // Asistent
  Role.ANALYST, // Analytik
  Role.FILE_ROOM, // Spisovna
];

export const OUTGOING_DOCUMENTS_ACCESS = [
  ...DOCUMENTS_ACCESS,
  Role.SOLVER, // Řešitel
  Role.ANONYMIZATION,
];

export const PROCESSED_DOCUMENTS_ACCESS = [
  ...DOCUMENTS_ACCESS,
  Role.SOLVER, // Řešitel
  Role.ANONYMIZATION,
];

export const UNPROCESSED_DELIVERED_DOCUMENTS_ACCESS = [
  Role.MAILROOM, // Podatelna/výpravna
  Role.LUSTRUM, // Lustrum
  Role.HEAD_OSV, // Vedoucí OSV
  Role.INDEX, // Rejstřík
];

function usePermission(accessMap: Map<Role, string[]>) {
  const { user } = useContext<UserContext<Me>>(UserContext);

  const isSuperAdmin = (user?.userRoles ?? []).some(
    (r) => r.role === Role.SUPERADMIN
  );

  const hasPermission = useEventCallback((element: string) => {
    const permittedElements = new Set<string>();

    if (isSuperAdmin) {
      return true;
    }

    // flat permitted elements into Set
    user?.userRoles.forEach(({ role }) => {
      const permittedForRole = accessMap.get(role!) ?? [];
      permittedForRole?.forEach((element) => permittedElements.add(element));
    });

    return permittedElements.has(element);
  });

  return { hasPermission };
}

export function useDeliveredDocumentPermission(
  document: DeliveredDocument | null
) {
  return usePermission(
    deliveredDocumentAccessMap.get(
      (document as DeliveredDocument)?.processed
        ? DeliveredDocumentState.PROCESSED
        : DeliveredDocumentState.UNPROCESSED
    ) as Map<Role, string[]>
  );
}

export const useInternalDocumentPermission = (
  document?: InternalDocument | null
) => {
  const { user } = useContext<UserContext<Me>>(UserContext);
  const detailContext = useContext(DetailContext);
  const contextualDocument = (document ?? detailContext?.source?.data) as
    | InternalDocument
    | undefined;
  const { hasPermission: hasMappedPermission } = usePermission(
    internalDocumentAccessMap
  );

  const isLustrum = (user?.userRoles ?? []).some(
    ({ role }) => role === Role.LUSTRUM
  );
  const isLustrumCourtNewFile =
    isLustrum &&
    contextualDocument?.esFile?.agenda === Agenda.COURT &&
    contextualDocument?.esFile?.state === EsFileState.NEW;

  const hasPermission = useEventCallback((element: string) => {
    if (hasMappedPermission(element)) {
      return true;
    }

    const canEditDocumentButton =
      element === UIElement.InternalDocument.EDIT_DOCUMENT_BUTTON &&
      isLustrumCourtNewFile;
    const isEditableInternalDocumentField = [
      UIElement.InternalDocument.SUMMARY,
      UIElement.InternalDocument.NOTE,
      UIElement.InternalDocument.PAGE_COUNT,
    ].some((editableField) => editableField === element);
    const canEditField =
      isLustrumCourtNewFile &&
      isEditableInternalDocumentField;

    return canEditDocumentButton || canEditField;
  });

  return { hasPermission };
};

export const useOutgoingDocumentPermission = () =>
  usePermission(outgoingDocumentAccessMap);
